/**
 * Slash Command Store
 * 
 * Zero prop drilling pattern ile command state management
 * - Command history (en son 20 komut)
 * - User shortcuts
 * - UI state (menu open/close, active path)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CommandHistoryItem, CommandShortcuts } from '../types/command-types';

interface SlashCommandStore {
  // ==================== UI State ====================
  isMenuOpen: boolean;
  isSpotlightOpen: boolean;            // Spotlight modal açık mı?
  activeCommandPath: string[];         // Current hierarchy path ["/model", "/model/llm"]
  selectedIndex: number;               // Currently selected suggestion index (keyboard navigation)
  spotlightQuery: string;              // Spotlight'ta yazılan query
  
  // ==================== Command History ====================
  commandHistory: CommandHistoryItem[];
  
  // ==================== Shortcuts ====================
  shortcuts: CommandShortcuts;         // { "/c": "/cart", "/s": "/shopping" }
  
  // ==================== UI Actions ====================
  openMenu: () => void;
  closeMenu: () => void;
  openSpotlight: () => void;
  closeSpotlight: () => void;
  setSpotlightQuery: (query: string) => void;
  setActiveCommandPath: (path: string[]) => void;
  setSelectedIndex: (index: number) => void;
  resetSelectedIndex: () => void;
  
  // ==================== History Actions ====================
  addToHistory: (item: Omit<CommandHistoryItem, 'timestamp' | 'usageCount'>) => void;
  clearHistory: () => void;
  
  // ==================== Shortcut Actions ====================
  addShortcut: (shortcut: string, command: string) => void;
  removeShortcut: (shortcut: string) => void;
  clearShortcuts: () => void;
  
  // ==================== Reset ====================
  reset: () => void;
  resetUIState: () => void; // Sadece UI state'i temizle (history ve shortcuts korunur)
}

const initialState = {
  isMenuOpen: false,
  isSpotlightOpen: false,
  activeCommandPath: [],
  selectedIndex: 0,
  spotlightQuery: '',
  commandHistory: [],
  shortcuts: {},
};

export const useSlashCommandStore = create<SlashCommandStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      // ==================== UI Actions ====================
      openMenu: () => set({ isMenuOpen: true }),
      
      closeMenu: () => set({ 
        isMenuOpen: false, 
        activeCommandPath: [],
        selectedIndex: 0,
      }),
      
      openSpotlight: () => set({ 
        isSpotlightOpen: true,
        selectedIndex: 0,
        spotlightQuery: '',
      }),
      
      closeSpotlight: () => set({ 
        isSpotlightOpen: false,
        selectedIndex: 0,
        spotlightQuery: '',
        activeCommandPath: [],
      }),
      
      setSpotlightQuery: (query) => set({ 
        spotlightQuery: query,
        selectedIndex: 0, // Reset selection when query changes
      }),
      
      setActiveCommandPath: (path) => set({ 
        activeCommandPath: path,
        selectedIndex: 0, // Reset selection when path changes
      }),
      
      setSelectedIndex: (index) => set({ selectedIndex: index }),
      
      resetSelectedIndex: () => set({ selectedIndex: 0 }),
      
      // ==================== History Actions ====================
      addToHistory: (item) => {
        const { commandHistory } = get();
        
        // Check if command already exists in history
        const existingIndex = commandHistory.findIndex((h) => h.id === item.id);
        
        let updatedHistory: CommandHistoryItem[];
        
        if (existingIndex !== -1) {
          // Update existing item - increase usage count and update timestamp
          updatedHistory = [...commandHistory];
          updatedHistory[existingIndex] = {
            ...updatedHistory[existingIndex],
            timestamp: Date.now(),
            usageCount: updatedHistory[existingIndex].usageCount + 1,
          };
        } else {
          // Add new item
          const newItem: CommandHistoryItem = {
            ...item,
            timestamp: Date.now(),
            usageCount: 1,
          };
          updatedHistory = [newItem, ...commandHistory];
        }
        
        // Keep only last 20 items, sorted by timestamp (most recent first)
        const sortedHistory = updatedHistory
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 20);
        
        set({ commandHistory: sortedHistory });
      },
      
      clearHistory: () => set({ commandHistory: [] }),
      
      // ==================== Shortcut Actions ====================
      addShortcut: (shortcut, command) => {
        const { shortcuts } = get();
        set({
          shortcuts: {
            ...shortcuts,
            [shortcut]: command,
          },
        });
      },
      
      removeShortcut: (shortcut) => {
        const { shortcuts } = get();
        const updatedShortcuts = { ...shortcuts };
        delete updatedShortcuts[shortcut];
        set({ shortcuts: updatedShortcuts });
      },
      
      clearShortcuts: () => set({ shortcuts: {} }),
      
      // ==================== Reset ====================
      reset: () => set(initialState),
      
      resetUIState: () => set({
        isMenuOpen: false,
        isSpotlightOpen: false,
        activeCommandPath: [],
        selectedIndex: 0,
        spotlightQuery: '',
        // History ve shortcuts korunur
      }),
    }),
    {
      name: 'slash-command-storage',
      // Only persist history and shortcuts (not UI state)
      partialize: (state) => ({
        commandHistory: state.commandHistory,
        shortcuts: state.shortcuts,
      }),
    }
  )
);

